import React from 'react';
import { DBGWithType } from '~modules/devices/components/DeviceHealthTable/types';
import { decommissionAction, resetAction } from '~components/Tables/DeviceTables/deviceTableActions';
import { TableRowAction } from '~components/Tables/DeviceTables/hooks/useTableRowActions';
import { DecommissionDeviceModal } from '~modules/devices/components/DeviceModals/DecommissionDeviceModal';
import { ResetModal } from '~modules/devices/components/DeviceModals/ResetModal';

interface UseDecommissionActionsProps {
  isEmployee: boolean;
  onRefresh: () => void;
}

/**
 * The row actions for taking hardware out of service, shared by the device, gateway and
 * beacon tables. Archiving is the everyday way to stop using something and keeps counting;
 * reset stops the counting and can be undone by unarchiving; permanently deleting is the
 * irreversible one that also blocks adding the same hardware again.
 */
export function useDecommissionActions({ isEmployee, onRefresh }: UseDecommissionActionsProps) {
  const [devicesToDecommission, setDevicesToDecommission] = React.useState<DBGWithType[]>([]);
  const [devicesToReset, setDevicesToReset] = React.useState<DBGWithType[]>([]);

  // Reset no longer needs the hardware to sit on a provisioning site: provisioning sites
  // have nothing to do with what is counted any more. Employees only, still.
  const actions: TableRowAction<DBGWithType>[] = [
    decommissionAction<DBGWithType>(setDevicesToDecommission),
    ...(isEmployee ? [resetAction<DBGWithType>(setDevicesToReset)] : []),
  ];

  const modals = (
    <>
      {devicesToDecommission.length > 0 && (
        <DecommissionDeviceModal
          devices={devicesToDecommission}
          onClose={() => setDevicesToDecommission([])}
          onRefresh={onRefresh}
        />
      )}
      {devicesToReset.length > 0 && (
        <ResetModal devices={devicesToReset} onClose={() => setDevicesToReset([])} onRefresh={onRefresh} />
      )}
    </>
  );

  return { actions, modals };
}
